"use client";

import { useState } from "react";
import { Copy, Check, Share2, Users } from "lucide-react";
import Link from "next/link";
import { TypeAnimation } from "react-type-animation";
import { useAuth } from "@/hooks/useAuth";
import { useGetCampaignUser, useGetCampaign } from "@/hooks/challenges";
import { CAMPAIGN_CONFIG } from "@/config";
import { Button } from "@/components/ui/button";
import { QuestLogo } from "@/components";
import { NumberIncrementer } from "@/components/number-incrementer";
import { ICONS } from "@/components/icons";
import Image from "next/image";

export default function ReferralPage() {
  const {
    isAuthenticated,
    login,
    isLoading: isAuthLoading,
  } = useAuth();
  const [copied, setCopied] = useState(false);

  // Fetch current user data for referral info
  const { data: currentUser, isLoading } = useGetCampaignUser(
    CAMPAIGN_CONFIG.id,
    CAMPAIGN_CONFIG.groupId,
  );

  // Fetch campaign data for referral tiers
  const { data: campaign } = useGetCampaign(
    CAMPAIGN_CONFIG.id,
    CAMPAIGN_CONFIG.groupId
  );

  const referralCode = currentUser?.referralData?.code || "";
  const pointsObtained = Number(currentUser?.referralData?.pointsObtained) || 0;
  const pointsGiven = Number(currentUser?.referralData?.pointsGiven) || 0;
  const referredBy = currentUser?.referralData?.referredBy;

  // Parse referral tiers from campaign metadata
  const referralTiers = campaign?.metadata?.referralTiers || {};
  const tierEntries = Object.entries(referralTiers);
  const firstTier = tierEntries[0];
  const minPoints = firstTier ? Number(firstTier[0]) : 0;
  const rewardPoints = firstTier ? Number(firstTier[1]) : 0;

  // Count qualifying referrals (each completed referral grants rewardPoints)
  const estimatedFriends = rewardPoints > 0 ? Math.floor(pointsObtained / rewardPoints) : 0;

  // Generate referral URL
  const referralUrl = referralCode
    ? `${typeof window !== "undefined" ? window.location.origin : ""}?referralCode=${referralCode}`
    : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShareTwitter = () => {
    const text = `Join me on Swapper Finance and earn rewards! Use my referral code: ${referralCode}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralUrl)}`;
    window.open(twitterUrl, "_blank");
  };

  const handleShareTelegram = () => {
    const text = `Join me on Swapper Finance and earn rewards!`;
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(referralUrl)}&text=${encodeURIComponent(text)}`;
    window.open(telegramUrl, "_blank");
  };

  const handleShareMore = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Swapper Finance",
          text: `Join me on Swapper Finance and earn rewards!`,
          url: referralUrl,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      handleCopy();
    }
  };

  // Loading state
  if (isLoading || isAuthLoading) {
    return (
      <div className="min-h-screen bg-swapper-light">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="h-48 bg-gray-200 animate-pulse rounded-2xl mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 animate-pulse rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated state
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-swapper-light flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full text-center">
          <div className="bg-gradient-to-br from-swapper-primary to-[#9F7FFF] p-5 rounded-2xl w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <Share2 className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-swapper-secondary mb-4">
            Sign in to view referrals
          </h2>
          <p className="text-swapper-secondary/70 mb-6">
            Sign in to access your referral code and start earning rewards by inviting friends.
          </p>
          <Button onClick={() => login()} className="w-full">
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  // Check if referral program is available
  const isReferralAvailable = minPoints > 0 && rewardPoints > 0;

  if (!isReferralAvailable && !isLoading) {
    return (
      <div className="min-h-screen bg-swapper-light flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full text-center">
          <div className="bg-gradient-to-br from-swapper-primary to-[#9F7FFF] p-5 rounded-2xl w-20 h-20 mx-auto mb-6 flex items-center justify-center opacity-50">
            <Share2 className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-swapper-secondary mb-4">
            Referral Program Coming Soon
          </h2>
          <p className="text-swapper-secondary/70 mb-6">
            The referral program is not currently available. Check back soon to
            start earning rewards by inviting friends!
          </p>
          <Link
            href="/"
            className="inline-block bg-swapper-primary hover:bg-swapper-primary/90 text-white font-medium px-8 py-3 rounded-xl transition-all duration-200 ease-out"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-swapper-light">
      {/* Header Section */}
      <div className="container mx-auto px-6 md:px-12 pt-12 pb-8 max-w-7xl">
        <h1 className="text-4xl md:text-5xl font-bold text-swapper-secondary mb-3">
          <TypeAnimation
            sequence={["Referral Program"]}
            wrapper="span"
            speed={50}
            cursor={false}
          />
        </h1>
        <p className="text-swapper-secondary/60 text-base md:text-lg">
          Invite your friends and earn rewards!
        </p>
      </div>

      {/* Main Grid Layout */}
      <div className="container mx-auto px-6 md:px-12 pb-12 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Card 1: Friends Referred */}
          <div className="relative bg-gradient-to-br from-purple-100 to-purple-50 rounded-3xl p-8 shadow-lg overflow-hidden border border-purple-200">
            {/* Background Visual */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-40">
              <Image
                src="/assets/referral/referral-visual.png"
                alt="Referral Visual"
                width={200}
                height={200}
                className="object-contain"
              />
            </div>

            {/* Content */}
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-swapper-primary/20 mb-4">
                <Users className="w-6 h-6 text-swapper-primary" />
              </div>

              <div className="mb-2">
                <NumberIncrementer
                  value={estimatedFriends}
                  className="text-6xl font-bold text-swapper-primary"
                  duration={1500}
                />
              </div>

              <p className="text-swapper-secondary/70 font-medium text-base">
                Friends Referred
              </p>
            </div>
          </div>

          {/* Left Column - Card 2: How Referrals Work */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-2xl font-bold text-swapper-secondary mb-6">
              How Referrals Work
            </h2>

            {/* Reward Info Card */}
            <div className="bg-gradient-to-r from-swapper-primary to-[#9F7FFF] rounded-2xl p-6 text-white mb-6">
              <div className="flex items-center justify-between">
                <span className="text-base font-medium">
                  Friend completes all Tier 1 tasks
                </span>
                <span className="text-base md:text-xl font-bold whitespace-nowrap">
                  +{rewardPoints} points
                </span>
              </div>
            </div>

            {/* Total Points Earned */}
            <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
              <span className="text-swapper-secondary/70 font-medium">
                Total Points Earned
              </span>
              <div className="flex items-center gap-2">
                <NumberIncrementer
                  value={pointsObtained}
                  className="text-2xl font-bold text-swapper-primary"
                  duration={1500}
                />
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-swapper-primary"
                >
                  <path
                    d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Right Column - Card 3: Your Referral Link (Full Width) */}
          <div className="md:col-span-2 bg-white rounded-3xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-2xl font-bold text-swapper-secondary mb-6">
              Your Referral Link
            </h2>

            {/* Referral URL Display */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="flex-1 bg-purple-50 rounded-xl px-6 py-4 border-2 border-purple-200">
                <p className="text-swapper-secondary font-mono text-sm md:text-base truncate">
                  {referralUrl || "Loading..."}
                </p>
              </div>
              <Button
                onClick={handleCopy}
                disabled={!referralCode}
                variant="primary"
                className="whitespace-nowrap"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* X/Twitter Button */}
              <button
                onClick={handleShareTwitter}
                disabled={!referralCode}
                className="flex items-center justify-center gap-3 bg-black text-white rounded-xl px-6 py-4 font-semibold hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none"
              >
                <img
                  src={ICONS.x}
                  alt="X"
                  width={20}
                  height={20}
                  className="w-5 h-5 brightness-0 invert"
                />
                <span>X</span>
              </button>

              {/* Telegram Button */}
              <button
                onClick={handleShareTelegram}
                disabled={!referralCode}
                className="flex items-center justify-center gap-3 bg-[#0088cc] text-white rounded-xl px-6 py-4 font-semibold hover:bg-[#006699] transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none"
              >
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" />
                </svg>
                <span>Telegram</span>
              </button>

              {/* More Button */}
              <button
                onClick={handleShareMore}
                disabled={!referralCode}
                className="flex items-center justify-center gap-3 bg-purple-100 text-swapper-primary rounded-xl px-6 py-4 font-semibold hover:bg-purple-200 transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none"
              >
                <Share2 className="w-5 h-5" />
                <span>More</span>
              </button>
            </div>
          </div>
        </div>

        {/* Referred By Section (if applicable) */}
        {referredBy && (
          <div className="mt-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200">
            <p className="text-sm text-green-800 font-medium">
              You were referred by:{" "}
              <span className="font-bold">
                {referredBy.slice(0, 8)}...{referredBy.slice(-6)}
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
